import { useEffect, useId, useMemo, useRef, useState } from 'react';
import { useQuery, useQueryClient } from '@tanstack/react-query';
import { useTranslation } from 'react-i18next';

import {
  credentialSchemasApi,
  type CredentialTypeSchemaResponse,
} from '@/api/credentialSchemas';
import { parseBitwarden } from '@/features/vault/transfer/bitwarden';
import type { TransferProgress } from '@/features/vault/transfer/exportVault';
import { decodeExport } from '@/features/vault/transfer/format';
import { importVault, type ImportReport } from '@/features/vault/transfer/importVault';
import { parseOnePasswordCsv } from '@/features/vault/transfer/onepassword';
import { parseOnePassword1pux } from '@/features/vault/transfer/onepassword1pux';
import {
  EntryList,
  ErrorBox,
  ProgressLine,
  skippedReason,
  transferErrorText,
} from '@/features/vault/transfer/shared';
import { TransferError, type ParsedImport } from '@/features/vault/transfer/types';

type ImportFormat = 'cerbes' | 'bitwarden' | 'onepassword' | 'onepassword1pux';

const FORMATS: Array<{ value: ImportFormat; labelKey: string; accept: string }> = [
  { value: 'cerbes', labelKey: 'transfer.import.formats.cerbes', accept: '.json,application/json' },
  { value: 'bitwarden', labelKey: 'transfer.import.formats.bitwarden', accept: '.json,application/json' },
  { value: 'onepassword', labelKey: 'transfer.import.formats.onepassword', accept: '.csv,text/csv' },
  { value: 'onepassword1pux', labelKey: 'transfer.import.formats.onepassword1pux', accept: '.1pux' },
];

/** Peste atât nu e un export de parole; evităm să încărcăm în memorie orice fișier. */
const MAX_FILE_BYTES = 64 * 1024 * 1024;

interface ImportPanelProps {
  tenantId: string;
  publicKey: Uint8Array;
  /** `viewer` nu poate crea credentiale — serverul ar refuza fiecare element. */
  canWrite: boolean;
  onBusyChange: (busy: boolean) => void;
}

export function ImportPanel({ tenantId, publicKey, canWrite, onBusyChange }: ImportPanelProps) {
  const { t } = useTranslation();
  const queryClient = useQueryClient();
  const formatId = useId();
  const passwordId = useId();
  const duplicatesId = useId();
  const fileInputRef = useRef<HTMLInputElement>(null);
  const abortRef = useRef<AbortController | null>(null);

  const [format, setFormat] = useState<ImportFormat>('cerbes');
  const [file, setFile] = useState<File | null>(null);
  const [password, setPassword] = useState('');
  const [parsed, setParsed] = useState<ParsedImport | null>(null);
  const [skipDuplicates, setSkipDuplicates] = useState(true);
  const [busy, setBusy] = useState(false);
  const [progress, setProgress] = useState<TransferProgress | null>(null);
  const [report, setReport] = useState<ImportReport | null>(null);
  const [error, setError] = useState<string | null>(null);

  const schemasQuery = useQuery({
    queryKey: ['credential-schemas', tenantId],
    queryFn: () => credentialSchemasApi.list(tenantId),
  });
  const schemasByType = useMemo(() => {
    const map: Record<string, CredentialTypeSchemaResponse> = {};
    for (const schema of schemasQuery.data ?? []) map[schema.type] = schema;
    return map;
  }, [schemasQuery.data]);

  // La închiderea dialogului, un import în curs se oprește după elementul curent.
  useEffect(() => () => abortRef.current?.abort(), []);

  const setWorking = (value: boolean) => {
    setBusy(value);
    onBusyChange(value);
  };

  const reset = () => {
    setFile(null);
    setPassword('');
    setParsed(null);
    setReport(null);
    setProgress(null);
    setError(null);
    if (fileInputRef.current) fileInputRef.current.value = '';
  };

  const analyze = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!file) return;
    setError(null);
    setWorking(true);
    try {
      if (file.size > MAX_FILE_BYTES) throw new TransferError('invalid_file');
      let result: ParsedImport;
      if (format === 'onepassword1pux') {
        // Arhivă zip: se citește binar și se despachetează în browser.
        result = await parseOnePassword1pux(await file.arrayBuffer());
      } else {
        const raw = await file.text();
        // Lasă starea „se analizează" să apară înainte de derivarea Argon2id.
        await new Promise((resolve) => setTimeout(resolve, 0));
        if (format === 'cerbes') {
          result = { items: (await decodeExport(raw, password)).credentials, skipped: [] };
        } else if (format === 'bitwarden') {
          result = parseBitwarden(raw);
        } else {
          result = parseOnePasswordCsv(raw);
        }
      }
      setPassword('');
      setParsed(result);
    } catch (err) {
      setError(transferErrorText(err, t));
    } finally {
      setWorking(false);
    }
  };

  const run = async () => {
    if (!parsed) return;
    setError(null);
    const controller = new AbortController();
    abortRef.current = controller;
    setWorking(true);
    try {
      const result = await importVault({
        tenantId,
        publicKey,
        schemasByType,
        items: parsed.items,
        skipDuplicates,
        onProgress: setProgress,
        signal: controller.signal,
      });
      // Raportul reunește ce a sărit parserul și ce a sărit importul.
      setReport({ ...result, skipped: [...parsed.skipped, ...result.skipped] });
      // Elementele în clar nu mai sunt necesare după import.
      setParsed(null);
    } catch (err) {
      setError(transferErrorText(err, t));
    } finally {
      setWorking(false);
      setProgress(null);
      void queryClient.invalidateQueries({ queryKey: ['credentials', tenantId] });
    }
  };

  if (!canWrite) {
    return <p className="text-sm text-[var(--text-muted)]">{t('transfer.import.viewer_denied')}</p>;
  }

  if (report) {
    return (
      <div className="space-y-3">
        <h3 className="display-title-sm">{t('transfer.import.report_title')}</h3>
        <ul className="text-sm text-[var(--text)] space-y-0.5" role="status">
          <li>{t('transfer.import.imported', { count: report.imported })}</li>
          <li>{t('transfer.import.skipped', { count: report.skipped.length })}</li>
          <li>{t('transfer.import.errors', { count: report.errors.length })}</li>
          {report.remaining > 0 && (
            <li style={{ color: 'var(--danger)' }}>
              {t('transfer.import.remaining', { count: report.remaining })}
            </li>
          )}
        </ul>
        <EntryList
          heading={t('transfer.import.skipped_title')}
          entries={report.skipped.map((s) => ({ title: s.title, detail: skippedReason(s, t) }))}
          untitled={t('transfer.untitled')}
        />
        <EntryList
          heading={t('transfer.import.errors_title')}
          entries={report.errors.map((f) => ({ title: f.title, detail: f.message }))}
          untitled={t('transfer.untitled')}
        />
        {format !== 'cerbes' && (
          <p className="text-xs text-[var(--text-muted)]">{t('transfer.import.plaintext_warning')}</p>
        )}
        <button type="button" className="btn-ghost" onClick={reset}>
          {t('transfer.import.another')}
        </button>
      </div>
    );
  }

  if (parsed) {
    return (
      <div className="space-y-3">
        <p className="text-sm text-[var(--text)]">
          {t('transfer.import.found', { count: parsed.items.length })}
        </p>
        <EntryList
          heading={t('transfer.import.unreadable_title', { count: parsed.skipped.length })}
          entries={parsed.skipped.map((s) => ({ title: s.title, detail: skippedReason(s, t) }))}
          untitled={t('transfer.untitled')}
        />
        <label htmlFor={duplicatesId} className="flex items-start gap-2 text-xs text-[var(--text-muted)]">
          <input
            id={duplicatesId}
            type="checkbox"
            className="mt-0.5"
            checked={skipDuplicates}
            onChange={(e) => setSkipDuplicates(e.target.checked)}
            disabled={busy}
          />
          <span>{t('transfer.import.skip_duplicates')}</span>
        </label>

        {busy && <ProgressLine progress={progress} />}
        {schemasQuery.isError && <ErrorBox>{t('transfer.errors.schema_missing')}</ErrorBox>}
        {error && <ErrorBox>{error}</ErrorBox>}

        <div className="flex gap-2 pt-1">
          {busy ? (
            <button type="button" className="btn-ghost" onClick={() => abortRef.current?.abort()}>
              {t('transfer.import.stop')}
            </button>
          ) : (
            <>
              <button
                type="button"
                className="btn-primary"
                onClick={() => void run()}
                disabled={parsed.items.length === 0 || !schemasQuery.data}
              >
                {t('transfer.import.start', { count: parsed.items.length })}
              </button>
              <button type="button" className="btn-ghost" onClick={reset}>
                {t('common.cancel')}
              </button>
            </>
          )}
        </div>
      </div>
    );
  }

  const accept = FORMATS.find((f) => f.value === format)?.accept;
  return (
    <form onSubmit={analyze} className="space-y-3">
      <p className="text-sm text-[var(--text-muted)]">{t('transfer.import.intro')}</p>

      <div>
        <label htmlFor={formatId} className="block text-xs font-medium text-[var(--text)] mb-1">
          {t('transfer.import.format_label')}
        </label>
        <select
          id={formatId}
          className="input"
          value={format}
          onChange={(e) => {
            setFormat(e.target.value as ImportFormat);
            reset();
          }}
          disabled={busy}
        >
          {FORMATS.map((f) => (
            <option key={f.value} value={f.value}>
              {t(f.labelKey)}
            </option>
          ))}
        </select>
        <p className="text-xs text-[var(--text-muted)] mt-1">{t(`transfer.import.format_hints.${format}`)}</p>
      </div>

      <div className="flex items-center gap-3">
        <input
          ref={fileInputRef}
          type="file"
          accept={accept}
          className="hidden"
          aria-label={t('transfer.import.choose_file')}
          onChange={(e) => {
            setFile(e.target.files?.[0] ?? null);
            setError(null);
          }}
          disabled={busy}
        />
        <button
          type="button"
          className="btn-outline"
          onClick={() => fileInputRef.current?.click()}
          disabled={busy}
        >
          {t('transfer.import.choose_file')}
        </button>
        <span className="text-xs text-[var(--text-muted)] truncate">
          {file ? file.name : t('transfer.import.no_file')}
        </span>
      </div>

      {format === 'cerbes' && (
        <div>
          <label htmlFor={passwordId} className="block text-xs font-medium text-[var(--text)] mb-1">
            {t('transfer.import.password_label')}
          </label>
          <input
            id={passwordId}
            type="password"
            className="input"
            autoComplete="off"
            value={password}
            onChange={(e) => setPassword(e.target.value)}
            disabled={busy}
            required
          />
        </div>
      )}

      {error && <ErrorBox>{error}</ErrorBox>}

      <div className="flex gap-2 pt-1">
        <button type="submit" className="btn-primary" disabled={busy || !file}>
          {busy ? t('transfer.import.analyzing') : t('transfer.import.analyze')}
        </button>
      </div>
    </form>
  );
}
